import type {
  Bounds2D,
  ClampSeatMapViewportDragPositionParams,
  ClampSeatMapViewportPositionParams,
  Point2D,
} from '../models/types';
import { DEFAULT_SEAT_MAP_EDGE_PADDING_PX } from '../constants/seatMap';

const getMapEdgePadding = (isMobile: boolean) =>
  isMobile ? DEFAULT_SEAT_MAP_EDGE_PADDING_PX.mobile : DEFAULT_SEAT_MAP_EDGE_PADDING_PX.desktop;

const clampMapAxisPosition = (
  position: number,
  scale: number,
  min: number,
  max: number,
  screenSize: number,
  padding: number
) => {
  const contentSize = (max - min) * scale;
  const center = screenSize / 2 - ((min + max) / 2) * scale;
  if (contentSize + padding * 2 <= screenSize) return center;

  const lowerLimit = screenSize - padding - max * scale;
  const upperLimit = padding - min * scale;
  return Math.max(lowerLimit, Math.min(upperLimit, position));
};

export const buildCameraFitScale = (
  bounds: Bounds2D,
  screenWidth: number,
  screenHeight: number,
  isMobile: boolean
) => {
  const padding = getMapEdgePadding(isMobile);
  const availableWidth = Math.max(1, screenWidth - padding * 2);
  const availableHeight = Math.max(1, screenHeight - padding * 2);
  const contentWidth = Math.max(1, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(1, bounds.maxY - bounds.minY);

  return Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
};

export const clampViewportPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile,
}: ClampSeatMapViewportPositionParams): Point2D => {
  const padding = getMapEdgePadding(isMobile);
  return {
    x: clampMapAxisPosition(position.x, scale, bounds.minX, bounds.maxX, screenWidth, padding),
    y: clampMapAxisPosition(position.y, scale, bounds.minY, bounds.maxY, screenHeight, padding),
  };
};

export const clampViewportPinchPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile,
}: ClampSeatMapViewportPositionParams): Point2D =>
  clampViewportPosition({ position, scale, bounds, screenWidth, screenHeight, isMobile });

export const clampViewportDragPosition = ({
  position,
  scale,
  bounds,
  screenWidth,
  screenHeight,
  isMobile = false,
}: ClampSeatMapViewportDragPositionParams): Point2D =>
  clampViewportPosition({ position, scale, bounds, screenWidth, screenHeight, isMobile });
